// frontend/src/pages/CommissionsPage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useApi from '../hooks/useApi';
import { 
    Plus, Search, Edit, Trash2, X, BadgeDollarSign, ChevronLeft, ChevronRight, User, Save, Loader2, List
} from 'lucide-react';

// --- COMPONENTE DE BUSCA INTELIGENTE DE CLIENTES ---
const ClientSearchInput = ({ value, onChange, onSelect, clientsList, disabled }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const filteredClients = useMemo(() => {
        // CORREÇÃO: Retorna vazio se o usuário não digitou nada, mantendo o padrão do sistema
        if (!value) return []; 
        
        const lowerVal = value.toLowerCase();
        const cleanVal = lowerVal.replace(/\D/g, ''); // Remove formatação (pontos/traços) para busca exata de CPF/CNPJ

        return clientsList.filter(c => {
            const matchName = c.name && c.name.toLowerCase().includes(lowerVal);
            const matchDoc = c.document_id && c.document_id.toLowerCase().includes(lowerVal);
            const matchDocNum = cleanVal && c.document_id && c.document_id.replace(/\D/g, '').includes(cleanVal);
            
            return matchName || matchDoc || matchDocNum;
        }).slice(0, 50); 
    }, [value, clientsList]);

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <input
                type="text"
                placeholder="Buscar cliente..."
                value={value}
                disabled={disabled}
                onChange={(e) => {
                    onChange(e.target.value);
                    setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onClick={() => setShowDropdown(true)}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-gray-50 disabled:text-gray-500"
            />
            {showDropdown && filteredClients.length > 0 && !disabled && (
                <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.map(c => (
                        <li 
                            key={c.id}
                            onClick={() => {
                                onSelect(c);
                                setShowDropdown(false);
                            }}
                            className="p-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                        >
                            <p className="font-bold text-sm text-gray-800 truncate">{c.name}</p>
                            <p className="text-[10px] text-gray-500 font-medium mt-0.5">
                                {c.document_id ? `Doc: ${c.document_id}` : 'Sem documento'}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const CommissionsPage = ({ currentUser }) => {
    const { apiFetch } = useApi();
    
    // Estados de Listagem
    const [commissionsList, setCommissionsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    
    // Estados do Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    
    // Dados do Formulário
    const [selectedUserId, setSelectedUserId] = useState('');
    const [commissionClients, setCommissionClients] = useState([{ tempId: Math.random(), client_id: '', client_name: '', tax_percentage: 0, commission_percentage: 0 }]);
    
    // Listas Auxiliares do Backend
    const [systemUsers, setSystemUsers] = useState([]);
    const [systemClients, setSystemClients] = useState([]);

    // Permissões
    const canView = currentUser?.is_super_user || currentUser?.permissions?.commissions_view;
    const canCreate = currentUser?.is_super_user || currentUser?.permissions?.commissions_create;
    const canEdit = currentUser?.is_super_user || currentUser?.permissions?.commissions_edit;
    const canDelete = currentUser?.is_super_user || currentUser?.permissions?.commissions_delete;

    // Busca listas auxiliares ao carregar a página
    useEffect(() => {
        if (canView) {
            // Busca de usuários
            apiFetch('/users')
                .then(res => {
                    const arr = Array.isArray(res) ? res : (res.data || []);
                    setSystemUsers(arr.filter(u => u.status === 'ativo' || !u.status));
                })
                .catch(console.error);
            
            // Busca de clientes (Removido o limit que estava bloqueando a requisição no backend)
            apiFetch('/general-clients')
                .then(res => {
                    const arr = Array.isArray(res) ? res : (res.data || res.clients || []);
                    setSystemClients(arr);
                })
                .catch(console.error);
        }
    }, [apiFetch, canView]);

    const fetchCommissions = useCallback(async (forceAll = false) => {
        if (!canView) return;
        setLoading(true);
        setHasSearched(true);
        try {
            const url = forceAll ? '/commissions' : `/commissions?search=${encodeURIComponent(searchQuery)}`;
            const data = await apiFetch(url);
            setCommissionsList(data || []);
        } catch (error) {
            console.error("Erro ao buscar comissões:", error);
            alert("Falha ao carregar as comissões.");
        } finally {
            setLoading(false);
        }
    }, [apiFetch, canView, searchQuery]);

    const handleSearchClick = () => {
        fetchCommissions(false);
    };

    const handleSearchAllClick = () => {
        setSearchQuery('');
        fetchCommissions(true);
    };

    // --- LÓGICA DO MODAL ---
    const openModal = async (commission = null) => {
        setFormError('');
        setSelectedUserId('');
        setCommissionClients([{ tempId: Math.random(), client_id: '', client_name: '', tax_percentage: 0, commission_percentage: 0 }]);
        
        if (commission) {
            setFormLoading(true);
            setIsModalOpen(true);
            try {
                // Busca os detalhes completos desta comissão (linhas de clientes)
                const details = await apiFetch(`/commissions/${commission.commission_id}`);
                setSelectedUserId(details.user_id);
                
                if (details.clients && details.clients.length > 0) {
                    setCommissionClients(details.clients.map(c => ({
                        tempId: Math.random(),
                        client_id: c.client_id,
                        client_name: c.client_name,
                        tax_percentage: c.tax_percentage,
                        commission_percentage: c.commission_percentage
                    })));
                }
            } catch (error) {
                alert("Erro ao carregar detalhes da comissão.");
                setIsModalOpen(false);
            } finally {
                setFormLoading(false);
            }
        } else {
            setIsModalOpen(true);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleAddClientRow = () => {
        setCommissionClients([...commissionClients, { tempId: Math.random(), client_id: '', client_name: '', tax_percentage: 0, commission_percentage: 0 }]);
    };

    const handleRemoveClientRow = (index) => {
        if (commissionClients.length > 1) {
            const newClients = [...commissionClients];
            newClients.splice(index, 1);
            setCommissionClients(newClients);
        }
    };

    const handleClientChange = (index, field, value) => {
        const newClients = [...commissionClients];
        newClients[index][field] = value;
        setCommissionClients(newClients);
    };

    const handleClientSelect = (index, clientObj) => {
        const newClients = [...commissionClients];
        newClients[index].client_id = clientObj.id;
        newClients[index].client_name = clientObj.name;
        setCommissionClients(newClients);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!selectedUserId) {
            setFormError("Por favor, selecione o usuário (beneficiário da comissão).");
            return;
        }

        // Filtra apenas linhas que têm um cliente selecionado
        const validClients = commissionClients.filter(c => c.client_id !== '');
        
        if (validClients.length === 0) {
            setFormError("Adicione pelo menos um cliente válido à regra.");
            return;
        }

        // Verifica duplicidade de clientes na mesma regra
        const clientIds = validClients.map(c => c.client_id);
        const hasDuplicates = new Set(clientIds).size !== clientIds.length;
        if (hasDuplicates) {
            setFormError("Você selecionou o mesmo cliente mais de uma vez. Remova a duplicidade.");
            return;
        }

        setFormLoading(true);
        try {
            await apiFetch('/commissions', {
                method: 'POST',
                body: {
                    user_id: selectedUserId,
                    clients: validClients
                }
            });
            closeModal();
            if (hasSearched) fetchCommissions(false);
        } catch (error) {
            setFormError(`Erro ao salvar: ${error.message}`);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (commissionId) => {
        if (window.confirm("Tem certeza que deseja excluir todas as regras de comissão deste usuário?")) {
            try {
                await apiFetch(`/commissions/${commissionId}`, { method: 'DELETE' });
                if (hasSearched) fetchCommissions(false);
            } catch (error) {
                alert(`Erro ao excluir: ${error.message}`);
            }
        }
    };

    if (!canView) {
        return <div className="p-8 text-center text-gray-500 font-bold">Você não tem permissão para acessar este módulo.</div>;
    }

    return (
        <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
            {/* CABEÇALHO */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-800 flex items-center gap-2">
                        <BadgeDollarSign className="text-blue-600" size={28} />
                        Regras de Comissão
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Gerencie os percentuais de comissão e impostos por usuário e cliente.</p>
                </div>
                {canCreate && (
                    <button 
                        onClick={() => openModal()} 
                        className="bg-blue-600 text-white px-4 py-2.5 rounded-lg font-bold shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-2 w-full md:w-auto justify-center"
                    >
                        <Plus size={18} /> Nova Regra
                    </button>
                )}
            </div>

            {/* FILTROS E BUSCA */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 relative w-full">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Pesquisar Beneficiário</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input 
                                type="text" 
                                placeholder="Nome do usuário ou login..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-2 w-full md:w-auto">
                        <button 
                            onClick={handleSearchClick} 
                            className="flex-1 md:flex-none bg-gray-800 text-white px-5 py-2.5 rounded-lg font-bold shadow-sm hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 h-[42px]"
                        >
                            <Search size={16} /> Buscar
                        </button>
                        <button 
                            onClick={handleSearchAllClick} 
                            className="flex-1 md:flex-none bg-white text-gray-700 border border-gray-300 px-5 py-2.5 rounded-lg font-bold shadow-sm hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 h-[42px]"
                        >
                            <List size={16} /> Ver Todos
                        </button>
                    </div>
                </div>
            </div>

            {/* LISTA DE RESULTADOS */}
            {hasSearched && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-4">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500 font-medium flex items-center justify-center gap-2">
                            <Loader2 className="animate-spin" size={20} /> Buscando regras...
                        </div>
                    ) : commissionsList.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 font-medium italic bg-gray-50/50">Nenhuma regra de comissão encontrada.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-100 text-gray-600 font-bold uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4 rounded-tl-lg">Beneficiário (Usuário)</th>
                                        <th className="px-6 py-4 text-center">Clientes Vinculados</th>
                                        <th className="px-6 py-4 text-center">Última Atualização</th>
                                        <th className="px-6 py-4 text-right rounded-tr-lg">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {commissionsList.map(comm => (
                                        <tr key={comm.commission_id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1.5 bg-blue-100 text-blue-700 rounded-full">
                                                        <User size={16} />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold">{comm.full_name || comm.username}</div>
                                                        {/* AQUI FOI REMOVIDO O @ DA LISTAGEM TAMBÉM */}
                                                        <div className="text-xs text-gray-500 font-mono">Login: {comm.username}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-bold text-xs">
                                                    {comm.total_clients} {comm.total_clients === 1 ? 'Cliente' : 'Clientes'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center text-gray-500">
                                                {comm.updated_at ? new Date(comm.updated_at.replace(' ', 'T')).toLocaleDateString('pt-BR') : '-'}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    {canEdit && (
                                                        <button 
                                                            onClick={() => openModal(comm)} 
                                                            className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors" 
                                                            title="Editar Regras"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button 
                                                            onClick={() => handleDelete(comm.commission_id)} 
                                                            className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors" 
                                                            title="Excluir Todas as Regras"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* MODAL DE CRIAÇÃO E EDIÇÃO AUMENTADO (max-w-[95vw] h-[95vh]) */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] h-[95vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300">
                        
                        {/* HEADER DO MODAL */}
                        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50 shrink-0 rounded-t-xl">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <BadgeDollarSign className="text-blue-600" />
                                {selectedUserId ? 'Editar Regras de Comissão' : 'Novas Regras de Comissão'}
                            </h2>
                            <button onClick={closeModal} disabled={formLoading} className="p-2 rounded-full hover:bg-gray-200 text-gray-500 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        {/* CORPO DO MODAL */}
                        <div className="p-6 overflow-y-auto flex-1 bg-white">
                            {formLoading ? (
                                <div className="flex flex-col items-center justify-center h-40 text-blue-600 gap-3">
                                    <Loader2 className="animate-spin" size={32} />
                                    <span className="font-bold">Carregando dados...</span>
                                </div>
                            ) : (
                                <>
                                    {formError && (
                                        <div className="p-3 mb-5 text-sm font-semibold text-red-700 bg-red-100 border border-red-200 rounded-lg">
                                            {formError}
                                        </div>
                                    )}

                                    {/* SELEÇÃO DO USUÁRIO BENEFICIÁRIO */}
                                    <div className="mb-8">
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                            1. Selecione o Usuário Beneficiário <span className="text-red-500">*</span>
                                        </label>
                                        <select 
                                            value={selectedUserId} 
                                            onChange={(e) => setSelectedUserId(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 font-medium"
                                        >
                                            <option value="">-- Selecione um usuário do sistema --</option>
                                            {systemUsers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {/* FORMATO ATUALIZADO AQUI (Sem @) */}
                                                    {user.full_name ? `${user.full_name} (${user.username})` : user.username}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-2">Este é o funcionário que receberá as comissões cadastradas abaixo.</p>
                                    </div>

                                    {/* TABELA DE CLIENTES E TAXAS */}
                                    <div>
                                        <div className="flex items-center justify-between mb-4 border-b border-gray-200 pb-2">
                                            <label className="block text-sm font-bold text-gray-700 uppercase tracking-wide">
                                                2. Clientes e Percentuais <span className="text-red-500">*</span>
                                            </label>
                                            <button 
                                                type="button" 
                                                onClick={handleAddClientRow} 
                                                className="text-sm font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded transition-colors"
                                            >
                                                <Plus size={16}/> Adicionar Linha
                                            </button>
                                        </div>

                                        <div className="border rounded-lg overflow-visible shadow-sm pb-10">
                                            <table className="w-full text-left text-sm whitespace-nowrap">
                                                <thead className="bg-gray-800 text-white font-bold uppercase text-xs">
                                                    <tr>
                                                        <th className="p-3 w-1/2">Cliente</th>
                                                        <th className="p-3 w-1/4 text-center">% Imposto a descontar</th>
                                                        <th className="p-3 w-1/4 text-center">% Comissão</th>
                                                        <th className="p-3 w-10 text-center">Ação</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                    {commissionClients.map((row, index) => (
                                                        <tr key={row.tempId} className="hover:bg-gray-50">
                                                            {/* COLUNA CLIENTE */}
                                                            <td className="p-2 relative min-w-[250px]">
                                                                <ClientSearchInput 
                                                                    value={row.client_name}
                                                                    onChange={(val) => handleClientChange(index, 'client_name', val)}
                                                                    onSelect={(client) => handleClientSelect(index, client)}
                                                                    clientsList={systemClients}
                                                                />
                                                            </td>

                                                            {/* COLUNA IMPOSTO */}
                                                            <td className="p-2">
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <input 
                                                                        type="number"
                                                                        min="0"
                                                                        max="100"
                                                                        step="0.01"
                                                                        value={row.tax_percentage}
                                                                        onChange={(e) => handleClientChange(index, 'tax_percentage', e.target.value)}
                                                                        className="w-20 p-2 text-center border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                                                                    />
                                                                    <span className="font-bold text-gray-500">%</span>
                                                                </div>
                                                            </td>

                                                            {/* COLUNA COMISSÃO */}
                                                            <td className="p-2">
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <input 
                                                                        type="number"
                                                                        min="0"
                                                                        max="100"
                                                                        step="0.01"
                                                                        value={row.commission_percentage}
                                                                        onChange={(e) => handleClientChange(index, 'commission_percentage', e.target.value)}
                                                                        className="w-20 p-2 text-center border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none bg-green-50 text-green-700 font-bold"
                                                                    />
                                                                    <span className="font-bold text-gray-500">%</span>
                                                                </div>
                                                            </td>

                                                            {/* COLUNA EXCLUIR */}
                                                            <td className="p-2 text-center">
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => handleRemoveClientRow(index)}
                                                                    disabled={commissionClients.length === 1} 
                                                                    className="p-2 text-red-500 hover:bg-red-100 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                                                                    title="Remover Cliente"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* FOOTER DO MODAL */}
                        <div className="flex justify-end gap-3 p-5 border-t border-gray-200 shrink-0 bg-gray-50 rounded-b-xl">
                            <button 
                                type="button" 
                                onClick={closeModal} 
                                disabled={formLoading}
                                className="px-5 py-2.5 font-bold text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleSave}
                                disabled={formLoading} 
                                className="px-5 py-2.5 font-bold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center gap-2"
                            >
                                {formLoading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} 
                                {formLoading ? 'Salvando...' : 'Salvar Regras'}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default CommissionsPage;